import { useCallback, useEffect, useRef, useState } from 'react';
import type { CorrectionReviewMatch } from '@orborix/shared-types';
import { patchCorrectionState } from '../../../lib/englishApi.js';
import { checkCorrectionReview } from '../../../lib/correctionReviewApi.js';
import { createReviewClock, postReviewTime, type ReviewTimeSource } from './reviewTime.js';
import {
  againLine,
  DONE_LINE,
  memoryLine,
  effortLine,
  openLine,
  retryLine,
  soloLine,
} from './reviewCoachLines.js';

/**
 * El repaso de correcciones (forma 2 de #468, elegida por Emmanuel el 08/10;
 * rápido desde el 16/10). Por corrección, dos vueltas:
 *   1. «ayuda»: la tarjeta muestra «Mejor así» y el alumno la dice. Práctica:
 *      no se guarda nada.
 *   2. «sin mirar»: la tarjeta ya no muestra la frase (va la pista en español)
 *      y lo dicho arriba queda TAPADO. Hay 3 intentos (Emmanuel, 15/10: el
 *      micrófono o el STT a veces oyen mal): bien al 1.º = «La sé» (sube de
 *      escalón; 3 vueltas de memoria = aprendida); al 2.º o 3.º = cuenta como
 *      bien sin subir (vuelve en 3 días); mal los 3, o tocar «Ver la frase» =
 *      «Repasar» (vuelve mañana). Un silencio no gasta intento; «Siguiente» sin
 *      decirla o cerrar no deciden.
 * El coach guía con su voz e indicaciones cortas (`reviewCoachLines`): al
 * abrir, en cada corrección, al pasar a sin mirar, cuando no salió y al
 * terminar (Emmanuel, 16/10). No hace una pregunta que encaje con la frase
 * (hasta el 16/10 la escribía el server para cada una): esperarla no sumaba
 * nada. El micrófono está listo mientras habla, y apretarlo lo calla. La
 * explicación del juego en español va debajo de su primera línea UNA vez por
 * cuenta (`guide`, y al pasar a sin mirar); después queda «¿Cómo se juega?».
 * La vara es la de las correcciones
 * (`compareCorrectionReview`, en el server): lo que la corrección cambió no se
 * perdona.
 */
/** Intentos sin mirar antes de «Repasar» (Emmanuel, 15/10). */
export const SOLO_TRIES = 3;

export interface CoachReviewItem {
  id: string;
  memberIds: ReadonlyArray<string>;
  said: string;
  corrected: string;
  explanation: string | null;
  /**
   * Qué decir sin mirar, en español, escrito para que se sepa quién y cuándo
   * (Emmanuel, 15/10); `null` = sin pista: la tarjeta va sin la línea en español.
   */
  cue: string | null;
  /**
   * La regla del error, sin la respuesta: sin mirar y con intentos, se muestra
   * después de un error (Emmanuel, 15/10). `null` = sin regla.
   */
  hint: string | null;
  /**
   * Ya se repasó antes y VUELVE (3, 7 o 16 días): arranca sin mirar, para que la
   * vuelta pruebe la memoria de verdad (Emmanuel, 15/10). La primera vez, mirándola.
   */
  returning?: boolean;
  /**
   * Una frase de la charla asistida que siguió sin intentarla (Emmanuel, 19/10),
   * o una palabra guardada dicha en su frase de ejemplo (`word`, Emmanuel 20/10),
   * no una corrección: `said` vacío, nada «corregido» que marcar.
   */
  kind?: 'phrase' | 'word';
  /** Sólo `kind: 'word'`: la palabra guardada (la tarjeta la nombra). */
  word?: string;
  /**
   * Es de un foco activo del Radar (Emmanuel, 19/10): la tarjeta lo marca. El
   * Radar sólo se lee; repasarla no lo toca.
   */
  radar?: { focusId: string; label: string };
}

export type ReviewRound = 'ayuda' | 'solo';
export type ReviewPhase =
  | 'ask'
  | 'recording'
  | 'checking'
  /** Celebración; después sigue sola. */
  | 'accepted'
  | 'failed'
  /** Tocó «Ver la frase» en la vuelta sin mirar. */
  | 'seen'
  | 'error'
  | 'done';

export type ReviewOutcome = 'memoria' | 'practicada';

export interface ReviewBubble {
  who: 'coach' | 'user';
  text: string;
  /**
   * De qué corrección es (su índice). La pantalla muestra sólo la última línea
   * del coach DE ESTA corrección: una sin pregunta mostraba la de la anterior.
   */
  index: number;
  /** Del alumno: cómo la dijo. Del coach: la explicación del juego en español (la primera vez). */
  note?: string;
  /** Burbuja del alumno dicha con la frase a la vista: tapada en la vuelta sin mirar. */
  coverable?: boolean;
}

/** La explicación del juego, en español, debajo de lo que dice el coach (la primera vez de la cuenta). */
export interface ReviewGuideCopy {
  guideHelp: string;
  guideSolo: string;
}

export interface ReviewResult {
  item: CoachReviewItem;
  outcome: ReviewOutcome;
  /** Bien sin mirar al 2.º o 3.º intento: cuenta como bien, no sube de escalón. */
  retry?: boolean;
  /** Del server: con esta, 3 vueltas de memoria → aprendida, no vuelve más. */
  learned?: boolean;
  /** `false` = el server no lo guardó. Mientras se guarda, `true` sin `nextReviewAt`. */
  saved: boolean;
  /** Cuándo vuelve, del server (no se recalcula acá: el escalón depende de la fila). */
  nextReviewAt: string | null;
}

export interface UseCoachReviewReturn {
  item: CoachReviewItem | null;
  index: number;
  /** Las tarjetas sin las descartadas. */
  total: number;
  /** El lugar de la tarjeta entre las que cuentan (1, 2, …): sin las descartadas antes. */
  position: number;
  /** Las tarjetas descartadas («Descartar esta frase»): fuera del progreso y del total. */
  discardedIds: ReadonlySet<string>;
  round: ReviewRound;
  phase: ReviewPhase;
  thread: ReadonlyArray<ReviewBubble>;
  /** La burbuja de la vuelta con ayuda está tapada ahora. */
  covering: boolean;
  match: CorrectionReviewMatch | null;
  heard: string | null;
  error: string | null;
  results: ReadonlyArray<ReviewResult>;
  /** La frase se ve en la tarjeta (con ayuda, o después de decidir la vuelta sin mirar). */
  phraseVisible: boolean;
  /** Intentos que quedan en la vuelta sin mirar (3 al empezar; un silencio no gasta). */
  triesLeft: number;
  /** La corrección a la vista ya quedó decidida (la tarjeta no dice «cuenta» sin esto). */
  decided: boolean;
  startRecording: () => void;
  stopRecording: (audio: Blob | null) => void;
  /** Vuelta con ayuda: seguir sin decirla (pasa a la vuelta sin mirar). */
  follow: () => void;
  repeat: () => void;
  seePhrase: () => void;
  next: () => void;
  /** «Descartar esta frase»: la tarjeta sale sin contar y sigue la próxima. */
  discard: () => void;
  celebrationDone: () => void;
  /**
   * Cerrar (la X o «Listo»): deja de escuchar resultados en vuelo y espera lo
   * que se está guardando, para que quien abrió el repaso lea lo decidido
   * (el cierre pinta el verde; el Inicio vuelve a pedir la lista).
   */
  close: () => Promise<ReadonlyArray<ReviewResult>>;
  /** Cuando se guardaron (o fallaron) los resultados de hasta ahora, sin cerrar el repaso. */
  settled: () => Promise<void>;
}

export function useCoachReview(
  items: ReadonlyArray<CoachReviewItem>,
  opts: {
    /**
     * Dice una línea del coach (la anterior, si sonaba, se corta). `afterCelebration`: la
     * del acierto sin mirar, que suena después del sonido del festejo (card #595).
     */
    speak: (line: string, opts?: { afterCelebration?: boolean }) => void;
    /** Calla al coach (o «Escuchar»): al apretar el micrófono. */
    hush: () => void;
    /** La explicación del juego (la primera vez de la cuenta); `null` = ya la vio. */
    guide: ReviewGuideCopy | null;
    /** Repaso de un foco del Radar: sus resultados no cuentan para el tope del día. */
    via?: 'radar';
    /**
     * Las palabras guardadas (la Bodega de la nave): las líneas del coach son las suyas y el resultado
     * no se programa (el server no devuelve cuándo vuelve).
     */
    words?: boolean;
    /**
     * Suma su tiempo activo a los minutos del día (card #603): el Repaso del día y el de la
     * Bodega, que son hablados. Sin esto (el cierre, el Radar, los juegos), no suma.
     */
    timed?: ReviewTimeSource;
  },
): UseCoachReviewReturn {
  const [index, setIndex] = useState(0);
  const [round, setRound] = useState<ReviewRound>(() => (items[0]?.returning ? 'solo' : 'ayuda'));
  const [phase, setPhase] = useState<ReviewPhase>(items.length > 0 ? 'ask' : 'done');
  const [thread, setThread] = useState<ReviewBubble[]>([]);
  const [covering, setCovering] = useState(false);
  const [match, setMatch] = useState<CorrectionReviewMatch | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResultsState] = useState<ReviewResult[]>([]);
  /** Las descartadas («Descartar esta frase», abajo). */
  const [discardedIds, setDiscardedIds] = useState<ReadonlySet<string>>(() => new Set());
  const resultsRef = useRef<ReviewResult[]>([]);
  const setResults = useCallback((f: (r: ReviewResult[]) => ReviewResult[]): void => {
    resultsRef.current = f(resultsRef.current);
    setResultsState(resultsRef.current);
  }, []);
  const savingRef = useRef<Array<Promise<void>>>([]);
  const closedRef = useRef(false);
  const decidedRef = useRef<Set<string>>(new Set());
  /** Intentos fallidos en la vuelta sin mirar de la corrección actual. */
  const [misses, setMisses] = useState(0);
  const startedRef = useRef<number>(-1);
  const clockRef = useRef(createReviewClock());
  const timed = opts.timed;
  const item = index < items.length ? items[index]! : null;
  const speak = opts.speak;
  const hush = opts.hush;
  const via = opts.via;
  const words = opts.words === true;
  const lines = words ? ('words' as const) : ('talks' as const);
  const guideRef = useRef(opts.guide);
  /** La explicación de «sin mirar» sale una vez (la primera vez que se llega). */
  const soloGuideShownRef = useRef(false);
  /** Cuántas veces no salió en este repaso (para variar lo que dice el coach). */
  const failsRef = useRef(0);

  /** El coach dice una línea: queda a la vista para esta corrección y suena. */
  const say = useCallback(
    (text: string, at: number, note?: string, afterCelebration = false): void => {
      setThread((t) => [...t, { who: 'coach', text, index: at, ...(note ? { note } : {}) }]);
      if (afterCelebration) speak(text, { afterCelebration });
      else speak(text);
    },
    [speak],
  );

  // Cada corrección arranca con el coach diciendo qué hacer (al abrir, además,
  // de qué se trata). La primera vez de la cuenta, con la explicación debajo.
  useEffect(() => {
    if (!item || startedRef.current === index) return;
    startedRef.current = index;
    const returning = item.returning === true;
    const guide = guideRef.current;
    let note: string | undefined;
    if (guide && index === 0) {
      if (returning) soloGuideShownRef.current = true;
      note = returning ? guide.guideSolo : guide.guideHelp;
    }
    say(openLine(index, returning, lines, item.kind === 'word'), index, note);
  }, [item, index, say, lines]);

  // Cada paso del repaso (grabar, el resultado, seguir) cuenta como actividad.
  useEffect(() => {
    clockRef.current.tick();
  }, [phase, index]);

  // Al terminar, el coach cierra (si quedó alguna: todas descartadas, no hubo repaso).
  useEffect(() => {
    if (phase === 'done' && items.length > discardedIds.size && startedRef.current >= 0)
      speak(DONE_LINE);
  }, [phase, items.length, discardedIds.size, speak]);

  /** No salió: el coach anima a intentarlo de nuevo (o avisa que vuelve mañana). */
  const sayRetry = useCallback(
    (silence: boolean, last: boolean): void => {
      if (last) {
        say(againLine(lines), index);
        return;
      }
      say(retryLine(failsRef.current, silence), index);
      if (!silence) failsRef.current += 1;
    },
    [say, index, lines],
  );

  const decide = useCallback(
    (it: CoachReviewItem, outcome: ReviewOutcome, retry = false): void => {
      if (closedRef.current || decidedRef.current.has(it.id)) return;
      decidedRef.current.add(it.id);
      const id = it.id;
      const patchEntry = (p: Partial<ReviewResult>): void =>
        setResults((r) => r.map((x) => (x.item.id === id ? { ...x, ...p } : x)));
      setResults((r) => [
        ...r,
        { item: it, outcome, ...(retry ? { retry } : {}), saved: true, nextReviewAt: null },
      ]);
      const action =
        outcome === 'practicada' ? 'review_again' : retry ? 'review_ok_retry' : 'review_ok';
      const ids = [...it.memberIds];
      // Las de «Decilas ahora» van con `ship`: el server no las programa.
      const sentVia = via ?? (words ? ('ship' as const) : undefined);
      const saving = (
        sentVia
          ? patchCorrectionState(ids, action, undefined, sentVia)
          : patchCorrectionState(ids, action)
      )
        .then(({ nextReviewAt, learned }) => {
          // Una palabra de «Decilas ahora» no se programa: no hay «cuándo vuelve».
          if (!nextReviewAt && !words) throw new Error('corrections/state: repaso sin nextReviewAt');
          patchEntry({ nextReviewAt: nextReviewAt ?? null, ...(learned ? { learned } : {}) });
        })
        .catch((err: unknown) => {
          console.warn('[review] saving the result failed', err);
          patchEntry({ saved: false });
        });
      savingRef.current.push(saving);
    },
    [setResults, via, words],
  );

  const toSolo = useCallback(
    (said: boolean): void => {
      if (!item) return;
      const guide = guideRef.current;
      let note: string | undefined;
      if (guide && !soloGuideShownRef.current) {
        soloGuideShownRef.current = true;
        note = guide.guideSolo;
      }
      say(soloLine(index, said), index, note);
      setRound('solo');
      setMisses(0);
      setCovering(true);
      setMatch(null);
      setHeard(null);
      setPhase('ask');
    },
    [item, index, say],
  );

  // «Siguiente» sin haberla dicho no decide (decisión de Emmanuel, 14/10: para
  // que cuente, hay que decirla): la corrección queda como estaba, como al cerrar.
  // «Descartar esta frase» (Emmanuel, 19/10): la tarjeta se va como si no hubiera
  // llegado —no cuenta, no vuelve al repaso— y sigue la próxima. El server la
  // marca y la anota (para revisar al escritor y al corrector).
  const discard = useCallback((): void => {
    if (!item || closedRef.current) return;
    if (phase === 'recording' || phase === 'checking') return;
    hush();
    const id = item.id;
    decidedRef.current.delete(id);
    setResults((r) => r.filter((x) => x.item.id !== id));
    savingRef.current.push(
      patchCorrectionState([...item.memberIds], 'review_discard').then(
        () => undefined,
        (err: unknown) => console.warn('[review] the discard was not saved', err),
      ),
    );
    setDiscardedIds((prev) => new Set(prev).add(id));
    nextRef.current();
  }, [item, phase, hush, setResults]);

  const next = useCallback((): void => {
    setCovering(false);
    setMatch(null);
    setHeard(null);
    setMisses(0);
    // La que vuelve arranca sin mirar; la primera vez, mirándola.
    setRound(items[index + 1]?.returning ? 'solo' : 'ayuda');
    if (index + 1 >= items.length) {
      setIndex(items.length);
      setPhase('done');
    } else {
      setIndex(index + 1);
      setPhase('ask');
    }
  }, [index, items]);
  const nextRef = useRef(next);
  nextRef.current = next;

  const startRecording = useCallback((): void => {
    if (phase === 'ask' || phase === 'failed' || phase === 'seen' || phase === 'error') {
      hush(); // cuando habla el alumno, el coach se calla
      setError(null);
      setPhase('recording');
    }
  }, [phase, hush]);

  const stopRecording = useCallback(
    (audio: Blob | null): void => {
      if (!item || closedRef.current) return;
      if (!audio) {
        setMatch({ state: 'silence', accepted: false, marks: [], missing: [], extra: [] });
        setHeard('');
        setPhase('failed');
        sayRetry(true, false);
        return;
      }
      setPhase('checking');
      checkCorrectionReview(item.id, audio)
        .then(({ heard: h, match: m }) => {
          if (closedRef.current) return; // cerró mientras escuchaba: no decide
          setHeard(h);
          setMatch(m);
          if (m.state === 'silence') {
            setPhase('failed');
            sayRetry(true, false);
            return;
          }
          const note = round === 'ayuda' ? 'Con la frase a la vista' : 'Sin mirar';
          if (m.accepted) {
            setThread((t) => [
              ...t,
              { who: 'user', text: h, note, coverable: round === 'ayuda', index },
            ]);
            if (round === 'solo') {
              // Al 1.º intento sube de escalón; al 2.º o 3.º cuenta como bien sin subir.
              decide(item, 'memoria', misses > 0);
              setCovering(false);
              // Al 2.º o 3.º intento no es «de memoria»: el coach festeja el esfuerzo (card #580).
              // Al 1.º intento suena el festejo: la voz va después, no encima (card #595).
              say(misses > 0 ? effortLine(index) : memoryLine(index), index, undefined, misses === 0);
            }
            setPhase('accepted');
            return;
          }
          let last = false;
          if (round === 'solo' && !decidedRef.current.has(item.id)) {
            // 3 intentos (Emmanuel, 15/10): a veces el micrófono o el STT oyen mal.
            // Recién mal los 3 es «Repasar»; hasta ahí la frase sigue tapada.
            const missed = misses + 1;
            setMisses(missed);
            if (missed >= SOLO_TRIES) {
              decide(item, 'practicada');
              setCovering(false);
              last = true;
            }
          }
          setPhase('failed');
          sayRetry(false, last);
        })
        .catch((err: unknown) => {
          if (closedRef.current) return;
          console.warn('[review] check failed', err);
          setError(err instanceof Error ? err.message : String(err));
          setPhase('error');
        });
    },
    [item, index, round, decide, misses, sayRetry, say],
  );

  const celebrationDone = useCallback((): void => {
    if (phase !== 'accepted') return;
    if (round === 'ayuda') toSolo(true);
    else next();
  }, [phase, round, toSolo, next]);

  const follow = useCallback(() => toSolo(false), [toSolo]);
  const repeat = useCallback((): void => {
    setMatch(null);
    setHeard(null);
    setError(null);
    setPhase('ask');
  }, []);
  const seePhrase = useCallback((): void => {
    if (!item) return;
    decide(item, 'practicada');
    setCovering(false);
    setPhase('seen');
    say(againLine(lines), index);
  }, [item, decide, say, index, lines]);

  // Cerrar en la vuelta sin mirar NO decide (como «Siguiente» sin decirla): la
  // corrección queda como estaba y vuelve cuando tocaba.
  const close = useCallback(async (): Promise<ReadonlyArray<ReviewResult>> => {
    if (timed && !closedRef.current) {
      clockRef.current.tick();
      const seconds = clockRef.current.seconds();
      if (seconds > 0)
        savingRef.current.push(
          postReviewTime(timed, seconds).catch((err: unknown) =>
            console.warn('[review] the review time was not saved', err),
          ),
        );
    }
    closedRef.current = true;
    await Promise.all(savingRef.current);
    return resultsRef.current;
  }, [timed]);

  const settled = useCallback(async (): Promise<void> => {
    await Promise.all(savingRef.current);
  }, []);

  const decided = item ? decidedRef.current.has(item.id) : false;
  const phraseVisible =
    round === 'ayuda' ||
    phase === 'seen' ||
    phase === 'accepted' ||
    (decided && phase === 'failed');

  return {
    triesLeft: Math.max(0, SOLO_TRIES - misses),
    item,
    index,
    // Sólo se descarta la tarjeta de turno y se sigue adelante: las descartadas
    // son todas de antes.
    total: items.length - discardedIds.size,
    position: index + 1 - discardedIds.size,
    discardedIds,
    round,
    phase,
    thread,
    covering,
    match,
    heard,
    error,
    results,
    phraseVisible,
    decided,
    startRecording,
    stopRecording,
    follow,
    repeat,
    seePhrase,
    next,
    discard,
    celebrationDone,
    close,
    settled,
  };
}
